import React, { useEffect, useState } from 'react';
import { FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import CustomTextField from '../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import {
  AUDIENCE_OPTIONS,
  MOCK_BLOCK_CODES,
  MOCK_LOYALTY_TIERS,
} from '../../../../constants/WelcomeMessageConst';
import CustomMultiSelect from '../../../../shared/form-controls/custom-multi-select/Custom-Multi-Select';
import CustomChipInput from '../../../../shared/form-controls/custom-chip-input/Custom-Chip-Input';
import { fetchLovData } from '../../../../utils/ServicesAPIUtils';
import './Message-Create-View.scss';

const Audience = ({
  formData,
  handleInputChange,
  originalRoomList,
  originalRoomTypeList,
  originalFloorList,
  isReadOnly = false,
  setFormData,
  hotelId,
  language,
  message,
}) => {
  const [selectedBlockCodes, setSelectedBlockCodes] = useState([]);
  const [selectedLoyaltyTiers, setSelectedLoyaltyTiers] = useState([]);
  const [audienceTypes, setAudienceTypes] = useState([]);
  const [loyaltyTiers, setLoyaltyTiers] = useState([]);
  const [blockCodes, setBlockCodes] = useState([]);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [blockCodeLoading, setBlockCodeLoading] = useState(false);

  useEffect(() => {
    const fetchLoyaltyTiers = async () => {
      if (!hotelId) return;

      setLoyaltyLoading(true);
      try {
        const dataWrapper = await fetchLovData(hotelId, language, 'loyalty');
        if (dataWrapper?.data?.getLov?.length > 0) {
          const firstItem = dataWrapper.data.getLov[0] || {};
          const loyaltyList = firstItem.lists?.find((list) => list.listName === 'loyalty');
          if (loyaltyList) {
            const langData = loyaltyList.languages.find((item) => item.lang === language);
            if (langData) {
              setLoyaltyTiers(langData.values || []);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching loyalty tiers:', error);
        setLoyaltyTiers(MOCK_LOYALTY_TIERS);
      } finally {
        setLoyaltyLoading(false);
      }
    };

    fetchLoyaltyTiers();
  }, [hotelId, language]);

  useEffect(() => {
    const fetchBlockCodes = async () => {
      if (!hotelId) return;

      setBlockCodeLoading(true);
      try {
        const dataWrapper = await fetchLovData(hotelId, language, 'blockcodes');
        if (dataWrapper?.data?.getLov?.length > 0) {
          const firstItem = dataWrapper.data.getLov[0] || {};
          const blockCodeList = firstItem.lists?.find((list) => list.listName === 'blockcodes');
          if (blockCodeList) {
            const langData = blockCodeList.languages.find((item) => item.lang === language);
            if (langData) {
              setBlockCodes(langData.values || []);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching block codes:', error);
        setBlockCodes(MOCK_BLOCK_CODES);
      } finally {
        setBlockCodeLoading(false);
      }
    };

    fetchBlockCodes();
  }, [hotelId, language]);

  useEffect(() => {
    if (Array.isArray(formData?.blockCodes?.value)) {
      setSelectedBlockCodes(formData.blockCodes.value);
    }
    if (Array.isArray(formData?.loyaltyTiers?.value)) {
      setSelectedLoyaltyTiers(formData.loyaltyTiers.value);
    }
  }, [formData?.blockCodes?.value, formData?.loyaltyTiers?.value]);

  useEffect(() => {
    if (Array.isArray(formData?.audienceType?.value) && formData.audienceType.value.length > 0) {
      const correctOrder = ['Rooms', 'Room Type', 'Floors', 'Block Code', 'Loyalty Tier'];

      const selectedTypes = formData.audienceType.value.map((option) => {
        if (typeof option === 'string') return option;
        return option.name || option;
      });

      const sortedTypes = correctOrder.filter((type) => selectedTypes.includes(type));

      setAudienceTypes(sortedTypes);

      if (sortedTypes.length > 1) {
        const initialRelations = {};
        for (let i = 1; i < sortedTypes.length; i++) {
          const relationKey = `${sortedTypes[i - 1]}`;
          if (!formData.audienceRelations?.value?.[relationKey.replaceAll(' ', '')]) {
            initialRelations[relationKey] = 'AND';
          }
        }
        if (Object.keys(initialRelations).length > 0) {
          handleInputChange(
            {
              ...formData.audienceRelations?.value,
              ...initialRelations,
            },
            'audienceRelations',
          );
        }
      }
    }
  }, [formData?.audienceType?.value]);

  useEffect(() => {
    const selected = formData?.audienceType?.value || [];
    const typeFieldMap = {
      Rooms: 'residentRoom',
      'Room Type': 'roomType',
      Floors: 'residentFloor',
      'Block Code': 'blockCodes',
      'Loyalty Tier': 'loyaltyTiers',
    };

    Object.entries(typeFieldMap).forEach(([label, fieldKey]) => {
      if (
        !selected?.includes(label) &&
        Array.isArray(formData[fieldKey]?.value) &&
        formData[fieldKey]?.value?.length > 0
      ) {
        handleInputChange([], fieldKey);
      }
    });
  }, [formData?.audienceType?.value]);

  const handleRelationChange = (index, value) => {
    const relationKey = `${audienceTypes[index - 1].replaceAll(' ', '')}`;
    const updatedRelations = {
      ...formData.audienceRelations?.value,
      [relationKey]: value,
    };
    handleInputChange(updatedRelations, 'audienceRelations');
  };

  const setAudienceTypeArray = (value) => {
    setAudienceTypes(value);
  };

  if (isReadOnly) {
    const getValuesForType = (type) => {
      switch (type) {
        case 'Rooms':
          return Array.isArray(formData.residentRoom.value) ? formData.residentRoom.value : [];
        case 'Room Type':
          return Array.isArray(formData.roomType.value) ? formData.roomType.value : [];
        case 'Floors':
          return Array.isArray(formData.residentFloor.value) ? formData.residentFloor.value : [];
        case 'Block Code':
          return Array.isArray(formData.blockCodes.value)
            ? formData.blockCodes.value
                .map((id) => {
                  const bc = blockCodes.find((b) => b.id === id);
                  return bc ? bc.name : null;
                })
                .filter((x) => x)
            : [];
        case 'Loyalty Tier':
          return Array.isArray(formData.loyaltyTiers.value)
            ? formData.loyaltyTiers.value
                .map((id) => {
                  const t = loyaltyTiers.find((tl) => tl.id === id);
                  return t ? t.name : null;
                })
                .filter((x) => x)
            : [];
        default:
          return [];
      }
    };

    const getRelationText = (index) => {
      if (index >= audienceTypes.length - 1) {
        return '';
      }
      const key = audienceTypes[index].replaceAll(' ', '');
      return formData.audienceRelations.value?.[key] || '';
    };

    return (
      <div className='row px-3 w-100 d-flex mt-4 form-scroll audience-view--container'>
        <div className='row px-3 w-100 d-flex mt-3'>
          <div className='col-md-6 mb-3'>
            <strong>Audience</strong>
            <p>{formData.audience.value || 'All'}</p>
          </div>
          {formData.audience.value === 'Custom' && (
            <div className='col-md-6 mb-3'>
              <strong>Audience Type</strong>
              <p>{audienceTypes.join(', ') || '-'}</p>
            </div>
          )}
          {formData.audience.value === 'Custom' && formData?.confirmationId?.value && (
            <div className='col-md-11 mb-3'>
              <strong>Confirmation IDs</strong>
              <p>{formData.confirmationId.value}</p>
            </div>
          )}
        </div>

        {formData.audience.value === 'Custom' &&
          audienceTypes.map((type, idx) => {
            const vals = getValuesForType(type);
            return (
              <React.Fragment key={`${type}-${idx}`}>
                <div className='row mb-3'>
                  <div className='col-md-12 mb-1'>
                    <strong>{type}</strong>
                  </div>
                  <div className='col-md-12'>
                    {type === 'Block Code' || type === 'Loyalty Tier' ? (
                      <div className='d-flex flex-wrap gap-2'>
                        {vals.map((val, i) => (
                          <div
                            key={i}
                            className='audience-chip'
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '4px 12px',
                              margin: '2px',
                              border: '1px solid #000',
                              borderRadius: '20px',
                              fontSize: '14px',
                              color: '#333',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {val}
                          </div>
                        ))}
                        {vals.length === 0 && <p>-</p>}
                      </div>
                    ) : (
                      <p>{vals.length > 0 ? vals.join(', ') : '-'}</p>
                    )}
                  </div>
                </div>

                {idx < audienceTypes.length - 1 && getRelationText(idx) && (
                  <div className='audience-relation--divider mb-4'>
                    <span>
                      {getRelationText(idx)
                        ?.toLowerCase()
                        ?.replace(/^./, (c) => c?.toUpperCase())}
                    </span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </div>
    );
  }

  return (
    <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
      <div className='row px-3 w-100 d-flex mt-3'>
        <div className='custom-upload col-md-12'>
          <RadioGroup
            row
            aria-label={formData?.audience?.fieldName}
            value={formData?.audience?.value}
            onChange={(e) => {
              if (!isReadOnly) {
                handleInputChange(e.target.value, 'audience');
                if (e.target.value === 'Custom') {
                  handleInputChange([], 'audienceType');
                  setAudienceTypes([]);
                } else if (e.target.value === 'All') {
                  handleInputChange([], 'audienceType');
                  handleInputChange([], 'residentRoom');
                  handleInputChange([], 'roomType');
                  handleInputChange([], 'residentFloor');
                  handleInputChange([], 'blockCodes');
                  handleInputChange([], 'loyaltyTiers');
                  handleInputChange({}, 'audienceRelations');
                  setAudienceTypes([]);
                }
              }
            }}
            className='document-type mb-3'
          >
            <FormControlLabel
              value='All'
              control={<Radio color='primary' disabled={isReadOnly} />}
              label='All'
              className='form-control-spacing right-margin'
            />
            <FormControlLabel
              value='Custom'
              control={<Radio color='primary' disabled={isReadOnly} />}
              label='Custom'
              className='form-control-spacing left-margin'
            />
          </RadioGroup>

          {formData?.audience?.value === 'Custom' && (
            <>
              <div className='row mb-3'>
                <div className='col-md-11'>
                  <CustomTextField
                    fullWidth
                    item={{
                      value: formData?.confirmationId?.value || '',
                      fieldName: 'Confirmation ID',
                      errors: formData?.confirmationId?.errors || {},
                      validations: isReadOnly ? [] :
                        !((formData?.audienceType?.value || []).filter(v => v !== 'All').length > 0)
                          ? ['required'] : [],
                    }}
                    handleInputChange={(e) => {
                      const value = e.value;
                      const errors = {};
                      let isValid = true;

                      if (/,{2,}/.test(value)) {
                        errors.consecutiveCommas = 'Consecutive commas are not allowed.';
                        isValid = false;
                      }

                      if (/[^a-zA-Z0-9,\-]/.test(value)) {
                        errors.invalidChars =
                          'Only alphanumeric characters, hyphens, and commas are allowed.';
                        isValid = false;
                      }
                      const segments = value
                        .split(',')
                        .map((s) => s.trim())
                        .filter((s) => s);
                      if (value.includes(',') && segments.length === 0) {
                        errors.invalidFormat =
                          'Invalid format. Please enter valid confirmation IDs.';
                        isValid = false;
                      }

                      if (segments.length > 0) {
                        const uniqueIds = new Set(segments);
                        if (segments.length !== uniqueIds.size) {
                          errors.duplicate = 'Duplicate confirmation IDs cannot be entered.';
                          isValid = false;
                        }
                      }

                      setFormData((prev) => ({
                        ...prev,
                        confirmationId: {
                          ...prev.confirmationId,
                          value: value,
                          errors: errors,
                        },
                      }));
                    }}
                    maxLength={500}
                    endAdornment={true}
                    characterCount={true}
                    disabled={isReadOnly}
                  />
                  {formData?.confirmationId?.errors &&
                    Object.keys(formData.confirmationId.errors).length > 0 && (
                      <div className='text-danger mt-1' style={{ fontSize: '0.875rem' }}>
                        {Object.values(formData.confirmationId.errors).map((error, index) => (
                          <div key={index}>{error}</div>
                        ))}
                      </div>
                    )}
                </div>
              </div>
              <div className='row mb-3'>
                <div className='col-md-6 mb-3'>
                  <CustomMultiSelect
                    item={{
                      ...formData?.audienceType,
                      fieldName: 'Select Audience Type(s)',
                      validations: isReadOnly ? [] :
                        !(formData?.confirmationId?.value && formData.confirmationId.value.trim())
                          ? ['required'] : [],
                      value: audienceTypes,
                      errors: formData?.audienceType?.errors || {},
                    }}
                    handleInputChange={(e) => {
                      if (!isReadOnly) {
                        setAudienceTypeArray(e.value);
                        const normalized = Array.isArray(e) ? e : e?.value || [];
                        setFormData((prev) => ({
                          ...prev,
                          audienceType: {
                            ...prev.audienceType,
                            value: normalized,
                            errors: {},
                          },
                        }));
                      }
                    }}
                    labelField='name'
                    valueField='id'
                    options={AUDIENCE_OPTIONS}
                    hideNullOption={true}
                    isMulti={true}
                    placeholder='Select Audience Type(s)'
                    isDisabled={isReadOnly}
                  />
                </div>
              </div>

              {audienceTypes.map((type, idx) => (
                <React.Fragment key={type}>
                  {idx > 0 && (
                    <div className='row mb-3'>
                      <div className='col-md-11'>
                        <div className='audience-relation-container d-flex justify-content-around align-items-center'>
                          <RadioGroup
                            row
                            aria-label={formData?.audience?.fieldName}
                            value={
                              formData?.audienceRelations?.value?.[
                                `${audienceTypes[idx - 1].replaceAll(' ', '')}`
                              ] || 'AND'
                            }
                            onChange={(e) => handleRelationChange(idx, e.target.value)}
                            className='mb-0'
                          >
                            <FormControlLabel
                              value='AND'
                              control={<Radio color='primary' />}
                              label='And'
                              className={
                                formData.audienceRelations?.value?.[
                                  audienceTypes[idx - 1].replaceAll(' ', '')
                                ] === 'AND'
                                  ? ''
                                  : 'document-type'
                              }
                            />
                            <FormControlLabel
                              value='OR'
                              control={<Radio color='primary' />}
                              label='Or'
                              className={
                                formData.audienceRelations?.value?.[
                                  audienceTypes[idx - 1].replaceAll(' ', '')
                                ] === 'OR'
                                  ? ''
                                  : 'document-type'
                              }
                            />
                          </RadioGroup>
                        </div>
                      </div>
                    </div>
                  )}

                  {type === 'Rooms' && (
                    <div className='row mb-3'>
                      <div className='col-md-6 mb-3'>
                        <CustomMultiSelect
                          item={{
                            ...formData?.residentRoom,
                            fieldName: 'Select Room(s)',
                            value: Array.isArray(formData?.residentRoom?.value)
                              ? formData.residentRoom.value
                              : [],
                            errors: formData?.audienceType?.errors || {},
                          }}
                          options={originalRoomList}
                          hideNullOption={true}
                          handleInputChange={(e) => {
                            const normalized = Array.isArray(e) ? e : e?.value || [];
                            handleInputChange(normalized, 'residentRoom');
                          }}
                          valueField='number'
                          labelField='number'
                          searchPlaceholder='Search Room(s)'
                          search='customSearch'
                          isSearchable
                          isDisabled={isReadOnly}
                        />
                      </div>
                    </div>
                  )}
                  {type === 'Room Type' && (
                    <div className='row mb-3'>
                      <div className='col-md-6 mb-3'>
                        <CustomMultiSelect
                          item={{
                            ...formData?.roomType,
                            fieldName: 'Select Room Type(s)',
                            value: Array.isArray(formData?.roomType?.value)
                              ? formData.roomType.value
                              : [],
                            errors: formData?.audienceType?.errors || {},
                          }}
                          options={originalRoomTypeList}
                          hideNullOption={true}
                          handleInputChange={(e) => {
                            const normalized = Array.isArray(e) ? e : e?.value || [];
                            handleInputChange(normalized, 'roomType');
                          }}
                          valueField='name'
                          labelField='name'
                          searchPlaceholder='Search Room Type(s)'
                          search='customSearch'
                          isSearchable
                          isDisabled={isReadOnly}
                        />
                      </div>
                    </div>
                  )}
                  {type === 'Floors' && (
                    <div className='row mb-3'>
                      <div className='col-md-6 mb-3'>
                        <CustomMultiSelect
                          item={{
                            ...formData?.residentFloor,
                            fieldName: 'Select Floor(s)',
                            value: Array.isArray(formData?.residentFloor?.value)
                              ? formData.residentFloor.value
                              : [],
                            errors: formData?.audienceType?.errors || {},
                          }}
                          options={originalFloorList}
                          hideNullOption={true}
                          handleInputChange={(e) => {
                            const normalized = Array.isArray(e) ? e : e?.value || [];
                            handleInputChange(normalized, 'residentFloor');
                          }}
                          valueField='name'
                          labelField='name'
                          searchPlaceholder='Search Floor(s)'
                          search='customSearch'
                          isSearchable
                          isDisabled={isReadOnly}
                        />
                      </div>
                    </div>
                  )}
                  {type === 'Block Code' && (
                    <div className='row mb-3'>
                      <div className='col-md-11 mb-3'>
                        <CustomChipInput
                          options={blockCodes}
                          labelField='name'
                          textFieldLabel='Block Codes'
                          textFieldPlaceholder='Select Block Code(s)'
                          formData={formData}
                          setFormData={setFormData}
                          formField='blockCodes'
                          value={formData?.blockCodes?.value || []}
                          isCloseOnSelect={false}
                          isClearable={false}
                        />
                        {blockCodeLoading && (
                          <p className='text-muted mt-2'>Loading block codes...</p>
                        )}
                        {!blockCodeLoading && blockCodes.length === 0 && (
                          <p className='text-muted mt-2'>No block codes available</p>
                        )}
                      </div>
                    </div>
                  )}
                  {type === 'Loyalty Tier' && (
                    <div className='row mb-3'>
                      <div className='col-md-11 mb-3'>
                        <CustomChipInput
                          options={loyaltyTiers}
                          labelField='name'
                          textFieldLabel='Loyalty Tiers'
                          textFieldPlaceholder='Select Loyalty Tier(s)'
                          formData={formData}
                          setFormData={setFormData}
                          formField='loyaltyTiers'
                          value={formData?.loyaltyTiers?.value || []}
                          isCloseOnSelect={false}
                          isClearable={false}
                        />
                        {loyaltyLoading && (
                          <p className='text-muted mt-2'>Loading loyalty tiers...</p>
                        )}
                        {!loyaltyLoading && loyaltyTiers.length === 0 && (
                          <p className='text-muted mt-2'>No loyalty tiers available</p>
                        )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Audience;
